import React, {useState, useEffect, useCallback} from 'react';
import {Flex, Text, Icon, PriorityIcon, Dropdown} from '@priority-software/priority-style-react';
import {SearchDropdown} from '../../../_legacy/FormHeader/components/Search/SearchDropdown';
import List from '../../../components/Common/List';
import styles from '../styles/printDialog.module.scss';
import texts from '../texts';
import {highlightText} from '../../../_legacy/library/utils/highlightText';
import {Label} from '../../../components/Common/Label';
import {getLang} from '../../../Localization';
import {fetchSysLangs, systemLang} from '../../../Localization/languages';

const NoResults = () => (
    <Dropdown.Item disabled>
        <Flex>
            <Label textColor="secondary-400" skin="paragraph-2">
                {texts.noResults}
            </Label>
        </Flex>
    </Dropdown.Item>
);

const groupHeader = title => (
    <Flex componentClasses={styles.dropdownListTitleWrapper} justifyContent="flex-start">
        <Icon icon={title === texts.html ? PriorityIcon.html : PriorityIcon.doc} />

        <div className={styles.dropdownListTitle}>{title}</div>
    </Flex>
);

const FormatItem = ({item, onClick, isLastIndex, index, inputValue, langs, isEnglishHTML}) => (
    <Dropdown.Item key={index} divider={!isLastIndex} index={index} onClick={() => onClick(item)}>
        <div>
            <Text
                skin="paragraph-2"
                componentClasses={styles.dropdownListItem}
                textColor="primary-700"
            >
                {highlightText(item.title, inputValue, true, false)}
            </Text>
            {!isEnglishHTML && (
                <Text skin="caption-2">{langs.find(l => l.lang === item.language)?.langtitle}</Text>
            )}
        </div>
    </Dropdown.Item>
);

const renderGroup = children => <Dropdown.Section gradient>{children}</Dropdown.Section>;

interface FormatListProps {
    wordTemplatesList: any[];
    formatsList: any[];
    onSelectFormat: (selectedFormat: any) => void;
    selectedFormat: any;
    toggleIsFormatSelected: (isFormatSelected: boolean) => void;

    onDropdownClose: () => void;
    isEnglishHTML: boolean;
}

const FormatsDropdown = ({
    wordTemplatesList,
    formatsList,
    onSelectFormat,
    selectedFormat,
    toggleIsFormatSelected,
    onDropdownClose,
    isEnglishHTML
}: FormatListProps) => {
    const [inputValue, setValue] = useState(selectedFormat ? selectedFormat.title : '');
    const [langs, setLangs] = useState([]);

    useEffect(() => {
        const fetchLangs = async () => {
            const langs: systemLang[] = await fetchSysLangs();
            setLangs(langs);
        };
        fetchLangs();
    }, []);

    const getFullList = () => {
        const data = {};
        if (wordTemplatesList.length > 0) {
            wordTemplatesList.forEach(template => {
                if (!template.language) {
                    template.language = getLang();
                }
            });
            data[texts.format] = wordTemplatesList;
        }
        if (formatsList.length > 0) {
            data[texts.html] = formatsList;
        }
        return data;
    };

    const getDataAsArray = () => {
        if (dataList) {
            const data = [];
            if (wordTemplatesList.length > 0) {
                data.push(...wordTemplatesList);
            }
            if (formatsList.length > 0) {
                data.push(...formatsList);
            }

            return data;
        }
        return [];
    };
    const fullList = getFullList();
    const [dataList, setDataList] = useState(fullList);

    const getSelectedFormatIndex = () => {
        if (dataList) {
            const data = getDataAsArray();
            return data.indexOf(selectedFormat);
        }
        return -1;
    };
    const [seletedIndex, setSeletedIndex] = useState(getSelectedFormatIndex());

    const handleChangeQuery = useCallback(val => {
        setValue(val);
        toggleIsFormatSelected(false);
        if (val && val !== '') {
            const filterdList = {};

            Object.keys(fullList).forEach(k => {
                const filtered = fullList[k].filter(
                    item => item.title && item.title.toLowerCase().includes(val.toLowerCase())
                );
                if (filtered.length > 0) {
                    filterdList[k] = filtered;
                }
            });
            setDataList(filterdList);
        } else {
            setSeletedIndex(-1);
            setDataList(fullList);
        }
    }, []);

    useEffect(() => {
        selectedFormat && setValue(selectedFormat.title);
        setSeletedIndex(getSelectedFormatIndex());
    }, [selectedFormat]);

    const handleCloseDropdown = () => {
        if (inputValue === '' || inputValue !== selectedFormat.title) {
            setValue(selectedFormat.title);
            toggleIsFormatSelected(true);
        }
        onDropdownClose();
        setDataList(fullList);
    };

    return (
        <SearchDropdown
            openOnFocus
            circularNavigation
            clearOnClose={false}
            closeOnClear={false}
            closeOnBlur={false}
            shouldHandleHomeAndEndPress
            onClose={handleCloseDropdown}
            selectedIndex={seletedIndex}
            inputProps={{
                value: inputValue,
                onChange: handleChangeQuery,
                componentClasses: styles.dropdownInput,
                returnFocus: false,
                displayClear: false
            }}
        >
            <Dropdown.Content componentClasses={styles.formatsListContent}>
                {Object.keys(dataList).length ? (
                    <List
                        itemProps={{onClick: onSelectFormat}}
                        autoHeight
                        autoHeightMax={400}
                        data={dataList}
                        renderGroup={renderGroup}
                        renderGroupHeader={groupHeader}
                        renderItem={props => (
                            <FormatItem
                                inputValue={inputValue}
                                {...props}
                                langs={langs}
                                isEnglishHTML={isEnglishHTML}
                            />
                        )}
                    />
                ) : (
                    <NoResults />
                )}
            </Dropdown.Content>
        </SearchDropdown>
    );
};

export default FormatsDropdown;
